/**
 * AI Agent side panels - Reports, Approvals (human-in-the-loop) and Memory.
 *
 * Each panel is self-contained: it owns its data fetching (plain axios
 * service layers, local state) and renders inside the chat column of the
 * AiAgent page when its tab is selected.
 */
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Icon } from "../../components/Icon/Icon";
import { colors } from "../../styles/colors";
import {
  REPORT_FREQUENCIES,
  ReportConfig,
  ReportFrequency,
  ReportSummary,
  Report,
  getReportConfig,
  saveReportConfig,
  generateReport,
  listReports,
  getReport,
} from "../../api/aiReports";
import ReportView from "./ReportView";
import {
  PendingAction,
  ActionHistoryItem,
  ActionStats,
  listPendingActions,
  listActionHistory,
  getActionStats,
  approveAction,
  rejectAction,
} from "../../api/aiCopilot";
import {
  MemoryFact,
  listMemories,
  saveMemoryNote,
  deleteMemory,
} from "../../api/aiMemory";
import { useAiPlanTier } from "../../hooks/useAiPlanTier";

// --- Shared styling helpers ---------------------------------------------------

const S: Record<string, React.CSSProperties> = {
  panel: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 26px 30px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  sectionTitle: {
    fontFamily: "Poppins-Medium",
    fontSize: 15,
    color: colors.primaryText,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  card: {
    border: `1px solid ${colors.borderLight}`,
    borderRadius: 12,
    padding: "14px 16px",
    background: "transparent",
  },
  muted: {
    fontFamily: "Poppins-Regular",
    fontSize: 12.5,
    color: colors.secondaryTextColor,
    lineHeight: 1.5,
  },
  row: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  chip: {
    fontFamily: "Poppins-Regular",
    fontSize: 12.5,
    padding: "6px 12px",
    borderRadius: 999,
    border: `1px solid ${colors.borderLight}`,
    background: "transparent",
    color: colors.secondaryTextColor,
    cursor: "pointer",
  },
  chipActive: {
    borderColor: colors.fleetBlue,
    color: colors.fleetBlue,
    background: "rgba(59,130,246,0.08)",
  },
  primaryBtn: {
    fontFamily: "Poppins-Medium",
    fontSize: 13,
    padding: "9px 16px",
    borderRadius: 10,
    border: "none",
    background: colors.fleetBlue,
    color: "#F8FAFC",
    cursor: "pointer",
  },
  ghostBtn: {
    fontFamily: "Poppins-Regular",
    fontSize: 13,
    padding: "8px 14px",
    borderRadius: 10,
    border: `1px solid ${colors.borderLight}`,
    background: "transparent",
    color: colors.secondaryTextColor,
    cursor: "pointer",
  },
  input: {
    fontFamily: "Poppins-Regular",
    fontSize: 13,
    padding: "9px 12px",
    borderRadius: 10,
    border: `1px solid ${colors.borderLight}`,
    background: "transparent",
    color: colors.primaryText,
    outline: "none",
    flex: 1,
    minWidth: 160,
  },
};

const riskColor = (risk: string): string => {
  switch (risk) {
    case "critical":
      return "#DC2626";
    case "high":
      return "#EF4444";
    case "medium":
      return "#F59E0B";
    default:
      return "#22C55E";
  }
};

const Spinner: React.FC = () => (
  <div style={{ ...S.muted, padding: "18px 4px" }}>Loading…</div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ ...S.muted, padding: "18px 4px" }}>{text}</div>
);

// --- REPORTS PANEL --------------------------------------------------------------

export const ReportsPanel: React.FC = () => {
  const { t } = useTranslation();
  const aiGating = useAiPlanTier();
  const [config, setConfig] = useState<ReportConfig | null>(null);
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [openReport, setOpenReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [recipientsInput, setRecipientsInput] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, list] = await Promise.all([getReportConfig(), listReports(20)]);
      // Backward compat: older API responses may lack `frequencies`.
      if (!cfg.frequencies || cfg.frequencies.length === 0) {
        cfg.frequencies = cfg.frequency ? [cfg.frequency] : ["weekly"];
      }
      setConfig(cfg);
      setRecipientsInput((cfg.recipients ?? []).join(", "));
      setReports(list);
    } catch {
      toast.error(t("aiAgent.reports.loadError", "Could not load AI reports"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggleFrequency = (f: ReportFrequency) => {
    if (!config) return;
    const has = config.frequencies.includes(f);
    // Never allow an empty selection - keep at least one cadence.
    const frequencies = has
      ? config.frequencies.filter((x) => x !== f)
      : [...config.frequencies, f];
    if (frequencies.length === 0) return;
    setConfig({ ...config, frequencies, frequency: frequencies[0] });
  };

  const toggleSection = (key: string) => {
    if (!config) return;
    const sections = config.sections.includes(key)
      ? config.sections.filter((s) => s !== key)
      : [...config.sections, key];
    setConfig({ ...config, sections });
  };

  const save = async () => {
    if (!config) return;
    const recipients = recipientsInput
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      const next = await saveReportConfig({
        frequencies: config.frequencies,
        sections: config.sections,
        recipients,
        enabled: config.enabled,
      });
      if (!next.frequencies || next.frequencies.length === 0) {
        next.frequencies = config.frequencies;
      }
      setConfig({ ...config, ...next, available_sections: config.available_sections });
      toast.success(t("aiAgent.reports.saved", "Report configuration saved"));
    } catch {
      toast.error(t("aiAgent.reports.saveError", "Could not save configuration"));
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    if (!config) return;
    setGenerating(true);
    try {
      // Pass the CURRENT UI selection so "Generate now" always reflects
      // exactly what the user sees - even before saving the config.
      const report = await generateReport({
        frequency: config.frequencies[0],
        sections: config.sections,
        send_email: false,
      });
      toast.success(t("aiAgent.reports.generated", "Report generated"));
      setOpenReport(report);
      setReports(await listReports(20));
    } catch {
      toast.error(t("aiAgent.reports.generateError", "Report generation failed"));
    } finally {
      setGenerating(false);
    }
  };

  const view = async (id: number) => {
    try {
      setOpenReport(await getReport(id));
    } catch {
      toast.error(t("aiAgent.reports.loadError", "Could not load AI reports"));
    }
  };

  if (loading) return <div style={S.panel}><Spinner /></div>;
  if (!config) {
    return (
      <div style={S.panel}>
        <Empty text={t("aiAgent.reports.unavailable", "AI reports are not available.")} />
      </div>
    );
  }

  if (openReport) {
    return <ReportView report={openReport} onBack={() => setOpenReport(null)} />;
  }

  return (
    <div style={S.panel}>
      {/* Configuration */}
      <div style={S.sectionTitle}>
        <Icon name="stars" width={17} height={17} color={colors.fleetBlue} />
        {t("aiAgent.reports.configTitle", "Automatic AI reports")}
      </div>
      <div style={S.card}>
        <div style={{ ...S.muted, marginBottom: 10 }}>
          {t(
            "aiAgent.reports.configHelp",
            "The AI generates a fleet report on the schedule below and emails it to the recipients."
          )}
        </div>

        <div style={{ ...S.row, marginBottom: 12 }}>
          {REPORT_FREQUENCIES.map((f) => {
            const isDailyLocked = f.id === "daily" && !aiGating.canDailyReport;
            return (
              <div
                key={f.id}
                style={{ position: "relative", display: "inline-block" }}
                title={
                  isDailyLocked
                    ? t(
                        "aiAgent.upgrade.autonomous",
                        "Vous avez besoin d'une mise \u00e0 niveau vers Autonomous pour acc\u00e9der \u00e0 cette fonctionnalit\u00e9",
                      )
                    : t("aiAgent.reports.freqMultiHint", "Multi-selection: activate several cadences")
                }
              >
                <button
                  type="button"
                  style={{
                    ...S.chip,
                    ...(config.frequencies.includes(f.id) && !isDailyLocked ? S.chipActive : {}),
                    ...(isDailyLocked ? { opacity: 0.4, cursor: "default" } : {}),
                  }}
                  onClick={() => !isDailyLocked && toggleFrequency(f.id)}
                >
                  {config.frequencies.includes(f.id) && !isDailyLocked ? "\u2713 " : ""}
                  {isDailyLocked ? "\uD83D\uDD12 " : ""}
                  {t(`aiAgent.reports.freq.${f.id}`, f.label)}
                </button>
              </div>
            );
          })}
          <button
            type="button"
            style={{ ...S.chip, ...(config.enabled ? S.chipActive : {}) }}
            onClick={() => setConfig({ ...config, enabled: !config.enabled })}
          >
            {config.enabled
              ? t("aiAgent.reports.enabled", "Enabled")
              : t("aiAgent.reports.disabled", "Disabled")}
          </button>
        </div>

        <div style={{ ...S.row, marginBottom: 12 }}>
          {(config.available_sections ?? []).map((s) => (
            <button
              key={s.key}
              type="button"
              style={{ ...S.chip, ...(config.sections.includes(s.key) ? S.chipActive : {}) }}
              onClick={() => toggleSection(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div style={{ ...S.row }}>
          <input
            style={S.input}
            value={recipientsInput}
            placeholder={t("aiAgent.reports.recipients", "Recipients (comma-separated emails)")}
            onChange={(e) => setRecipientsInput(e.target.value)}
          />
          <button type="button" style={S.primaryBtn} disabled={saving} onClick={save}>
            {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
          </button>
          <button type="button" style={S.ghostBtn} disabled={generating} onClick={generateNow}>
            {generating
              ? t("aiAgent.reports.generating", "Generating…")
              : t("aiAgent.reports.generateNow", "Generate now")}
          </button>
        </div>
      </div>

      {/* History */}
      <div style={S.sectionTitle}>
        <Icon name="fileStack" width={17} height={17} color={colors.fleetBlue} />
        {t("aiAgent.reports.historyTitle", "Generated reports")}
      </div>
      {reports.length === 0 ? (
        <Empty text={t("aiAgent.reports.none", "No reports generated yet.")} />
      ) : (
        reports.map((r) => (
          <div
            key={r.id}
            style={{ ...S.card, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
            onClick={() => view(r.id)}
          >
            <Icon name="fileStack" width={18} height={18} color={colors.secondaryTextColor} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: "Poppins-Medium", fontSize: 13.5, color: colors.primaryText }}>
                {r.title}
              </div>
              <div style={S.muted}>
                {r.period_label} · {r.frequency}
                {r.emailed_at ? ` · ${t("aiAgent.reports.emailed", "emailed")}` : ""}
              </div>
            </div>
            <Icon name="chevronRight" width={16} height={16} color={colors.secondaryTextColor} />
          </div>
        ))
      )}
    </div>
  );
};

// --- APPROVALS PANEL --------------------------------------------------------------

export const ApprovalsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingAction[]>([]);
  const [history, setHistory] = useState<ActionHistoryItem[]>([]);
  const [stats, setStats] = useState<ActionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h, s] = await Promise.all([
        listPendingActions(),
        listActionHistory(25),
        getActionStats(),
      ]);
      setPending(p);
      setHistory(h);
      setStats(s);
    } catch {
      toast.error(t("aiAgent.approvals.loadError", "Could not load AI actions"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const decide = async (action: PendingAction, approve: boolean) => {
    setBusyId(action.action_id);
    try {
      if (approve) {
        await approveAction(action.action_id);
        toast.success(t("aiAgent.approvals.approved", "Action approved"));
      } else {
        const reason =
          window.prompt(
            t("aiAgent.approvals.rejectReason", "Reason for rejection (optional):")
          ) ?? "";
        await rejectAction(action.action_id, reason);
        toast.success(t("aiAgent.approvals.rejected", "Action rejected"));
      }
      await refresh();
    } catch {
      toast.error(t("aiAgent.approvals.decideError", "Could not update the action"));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div style={S.panel}><Spinner /></div>;

  return (
    <div style={S.panel}>
      {stats && (
        <div style={{ ...S.row }}>
          {(
            [
              ["pending", t("aiAgent.approvals.pending", "Pending")],
              ["approved", t("aiAgent.approvals.approvedCount", "Approved")],
              ["rejected", t("aiAgent.approvals.rejectedCount", "Rejected")],
              ["auto_approved", t("aiAgent.approvals.auto", "Auto")],
            ] as [keyof ActionStats, string][]
          ).map(([key, label]) => (
            <div key={key} style={{ ...S.card, padding: "10px 16px" }}>
              <div style={{ fontFamily: "Poppins-Medium", fontSize: 17, color: colors.primaryText }}>
                {stats[key]}
              </div>
              <div style={S.muted}>{label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={S.sectionTitle}>
        <Icon name="stars" width={17} height={17} color={colors.fleetBlue} />
        {t("aiAgent.approvals.pendingTitle", "Actions awaiting your approval")}
      </div>

      {pending.length === 0 ? (
        <Empty text={t("aiAgent.approvals.none", "No actions to approve. The AI queues risky actions here instead of executing them.")} />
      ) : (
        pending.map((a) => (
          <div key={a.action_id} style={{ ...S.card }}>
            <div style={{ ...S.row, justifyContent: "space-between" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: riskColor(a.risk_level),
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontFamily: "Poppins-Medium", fontSize: 13.5, color: colors.primaryText }}>
                    {a.action_type}
                  </span>
                  <span style={{ ...S.muted }}>· {a.risk_level}</span>
                </div>
                <div style={{ ...S.muted, marginTop: 4 }}>{a.description}</div>
                <div style={{ ...S.muted, marginTop: 2, fontSize: 11.5 }}>
                  {a.requested_by} · {a.created_at}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  style={{ ...S.primaryBtn, background: "#16A34A" }}
                  disabled={busyId === a.action_id}
                  onClick={() => decide(a, true)}
                >
                  {t("aiAgent.approvals.approve", "Approve")}
                </button>
                <button
                  type="button"
                  style={{ ...S.ghostBtn, borderColor: "rgba(239,68,68,0.5)", color: "#EF4444" }}
                  disabled={busyId === a.action_id}
                  onClick={() => decide(a, false)}
                >
                  {t("aiAgent.approvals.reject", "Reject")}
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      <div style={S.sectionTitle}>
        <Icon name="fileStack" width={17} height={17} color={colors.fleetBlue} />
        {t("aiAgent.approvals.historyTitle", "Recent decisions")}
      </div>
      {history.length === 0 ? (
        <Empty text={t("aiAgent.approvals.noHistory", "No AI actions recorded yet.")} />
      ) : (
        history.map((h) => (
          <div key={h.action_id} style={{ ...S.card, padding: "10px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{ width: 8, height: 8, borderRadius: 999, background: riskColor(h.risk_level), flexShrink: 0 }}
              />
              <span style={{ fontFamily: "Poppins-Medium", fontSize: 13, color: colors.primaryText }}>
                {h.action_type}
              </span>
              <span style={S.muted}>· {h.status}</span>
              {h.approved_by && <span style={S.muted}>· {h.approved_by}</span>}
            </div>
            <div style={{ ...S.muted, marginTop: 3 }}>{h.description}</div>
            {h.rejection_reason && (
              <div style={{ ...S.muted, marginTop: 2, color: "#EF4444" }}>
                {h.rejection_reason}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

// --- MEMORY PANEL -----------------------------------------------------------------

export const MemoryPanel: React.FC = () => {
  const { t } = useTranslation();
  const [memories, setMemories] = useState<MemoryFact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("");
  const [fact, setFact] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMemories(100);
      setMemories(data.memories);
      setTotal(data.total);
    } catch {
      toast.error(t("aiAgent.memory.loadError", "Could not load AI memory"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addNote = async () => {
    if (!entity.trim() || fact.trim().length < 5) return;
    setSavingNote(true);
    try {
      await saveMemoryNote(entity.trim(), fact.trim());
      toast.success(t("aiAgent.memory.noteSaved", "Note saved"));
      setEntity("");
      setFact("");
      await refresh();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      toast.error(msg || t("aiAgent.memory.noteError", "Could not save the note"));
    } finally {
      setSavingNote(false);
    }
  };

  const forget = async (m: MemoryFact) => {
    const label = m.entity_name ?? `#${m.memory_id}`;
    if (
      !window.confirm(
        t("aiAgent.memory.confirmDelete", "Delete this memorized fact about {{label}}?", { label })
      )
    ) {
      return;
    }
    try {
      await deleteMemory(m.memory_id);
      toast.success(t("aiAgent.memory.deleted", "Fact deleted"));
      setMemories((prev) => prev.filter((x) => x.memory_id !== m.memory_id));
      setTotal((v) => Math.max(0, v - 1));
    } catch {
      toast.error(t("aiAgent.memory.deleteError", "Could not delete the fact"));
    }
  };

  if (loading) return <div style={S.panel}><Spinner /></div>;

  return (
    <div style={S.panel}>
      <div style={S.sectionTitle}>
        <Icon name="sparkles" width={17} height={17} color={colors.fleetBlue} />
        {t("aiAgent.memory.title", "What the AI remembers")}
        <span style={{ ...S.muted, fontFamily: "Poppins-Regular" }}>
          · {total} {t("aiAgent.memory.facts", "facts")}
        </span>
      </div>
      <div style={S.muted}>
        {t(
          "aiAgent.memory.help",
          "The assistant memorizes recurring patterns about your drivers and vehicles to give better answers. You can delete any fact at any time (GDPR)."
        )}
      </div>

      {/* Manual note */}
      <div style={S.card}>
        <div style={{ ...S.row }}>
          <input
            style={{ ...S.input, maxWidth: 220 }}
            value={entity}
            placeholder={t("aiAgent.memory.entityPh", "Driver name or plate")}
            onChange={(e) => setEntity(e.target.value)}
          />
          <input
            style={S.input}
            value={fact}
            placeholder={t("aiAgent.memory.factPh", "Note to remember (min. 5 characters)")}
            onChange={(e) => setFact(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addNote()}
          />
          <button
            type="button"
            style={S.primaryBtn}
            disabled={savingNote || !entity.trim() || fact.trim().length < 5}
            onClick={addNote}
          >
            {savingNote ? t("common.saving", "Saving…") : t("aiAgent.memory.addNote", "Add note")}
          </button>
        </div>
      </div>

      {memories.length === 0 ? (
        <Empty text={t("aiAgent.memory.none", "The AI hasn't memorized anything yet.")} />
      ) : (
        memories.map((m) => (
          <div key={m.memory_id} style={{ ...S.card, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <Icon
              name={m.entity_type === "vehicle" ? "vehicule" : "collaborateur"}
              width={18}
              height={18}
              color={colors.secondaryTextColor}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "Poppins-Medium", fontSize: 13, color: colors.primaryText }}>
                  {m.entity_name ?? t("aiAgent.memory.unknown", "Unknown")}
                </span>
                <span style={S.muted}>
                  · {m.source === "manual"
                    ? t("aiAgent.memory.manual", "manual note")
                    : t("aiAgent.memory.auto", "learned")}
                  {" · "}
                  {t("aiAgent.memory.importance", "importance")} {m.importance_score}/10
                </span>
              </div>
              <div style={{ ...S.muted, marginTop: 3, color: colors.primaryText, fontSize: 13 }}>
                {m.fact}
              </div>
            </div>
            <button
              type="button"
              style={{ ...S.ghostBtn, padding: "6px 10px", borderColor: "rgba(239,68,68,0.4)" }}
              title={t("aiAgent.memory.delete", "Delete")}
              onClick={() => forget(m)}
            >
              <Icon name="trash" width={15} height={15} color="#EF4444" />
            </button>
          </div>
        ))
      )}
    </div>
  );
};
